import { create } from 'zustand'
import { api, setApiToken, User, Org, Team, Folder, FileWithVersion } from '@/services/api'

// Track the latest folder fetch request to prevent race conditions
let latestFolderFetchId = 0

interface AppState {
  user: User | null
  orgs: Org[]
  currentOrg: Org | null
  teams: Team[]
  accessToken: string | null
  currentFolder: Folder | null
  folderContents: {
    folders: Folder[]
    files: FileWithVersion[]
    path: Folder[]
  }
  isLoading: boolean
  error: string | null

  setAccessToken: (token: string | null) => void
  fetchUser: () => Promise<void>
  refreshOrgs: () => Promise<void>
  setCurrentOrg: (org: Org | null) => void
  fetchTeams: () => Promise<void>
  fetchFolderContents: (orgId: string, folderId: string) => Promise<void>
  setCurrentFolder: (folder: Folder | null) => void
  clearError: () => void
}

export const useAppStore = create<AppState>((set, get) => ({
  user: null,
  orgs: [],
  currentOrg: null,
  teams: [],
  accessToken: null,
  currentFolder: null,
  folderContents: {
    folders: [],
    files: [],
    path: [],
  },
  isLoading: false,
  error: null,

  setAccessToken: (token) => {
    setApiToken(token)
    set({ accessToken: token })
  },

  fetchUser: async () => {
    try {
      console.log('[Freehold][Store] fetchUser: start')
      set({ isLoading: true, error: null })
      const data = await api.me.get()
      console.log('[Freehold][Store] fetchUser: success', {
        hasUser: !!data.user,
        email: data.user?.email,
        display_name: data.user?.display_name,
        orgCount: data.orgs?.length,
      })
      set({
        user: data.user,
        orgs: data.orgs,
        currentOrg: data.orgs[0] || null,
        isLoading: false,
      })

		if (typeof window !== 'undefined' && data.my_files_org_id) {
			window.localStorage.setItem('myFilesOrgId', data.my_files_org_id)
		}
      if (data.orgs[0]) {
        get().fetchTeams()
      }
    } catch (error) {
      console.log('[Freehold][Store] fetchUser: error', {
        message: (error as Error)?.message,
        stack: (error as Error)?.stack,
      })
      set({ error: (error as Error).message, isLoading: false })
    }
  },

  refreshOrgs: async () => {
    try {
      const data = await api.orgs.list()
      const { currentOrg } = get()
      const nextCurrentOrg =
        (currentOrg && data.orgs.find((o) => o.id === currentOrg.id)) || data.orgs[0] || null

      set({ orgs: data.orgs, currentOrg: nextCurrentOrg })
      if (nextCurrentOrg && (!currentOrg || nextCurrentOrg.id !== currentOrg.id)) {
        get().fetchTeams()
      }
    } catch (error) {
      console.error('Failed to refresh orgs:', error)
    }
  },

  setCurrentOrg: (org) => {
    set({ currentOrg: org, teams: [], currentFolder: null })
    if (org) {
      get().fetchTeams()
    }
  },

  fetchTeams: async () => {
    const { currentOrg } = get()
    if (!currentOrg) return

    try {
      const data = await api.teams.my(currentOrg.id)
      set({ teams: data.teams })
    } catch (error) {
      console.error('Failed to fetch teams:', error)
    }
  },

  fetchFolderContents: async (orgId, folderId) => {
    if (!orgId) return

    // Increment and capture the fetch ID to handle race conditions
    const fetchId = ++latestFolderFetchId

    try {
      set({ isLoading: true, error: null })
      const data = await api.folders.contents(orgId, folderId)
      
      // Only update state if this is still the latest request
      // This prevents stale data from overwriting newer data
      if (fetchId !== latestFolderFetchId) {
        console.log('[Freehold][Store] Ignoring stale folder fetch', { fetchId, latestFolderFetchId, folderId })
        return
      }
      
      set({
        currentFolder: data.folder,
        folderContents: {
          folders: data.folders || [],
          files: data.files || [],
          path: data.path || [],
        },
        isLoading: false,
      })
    } catch (error) {
      // Only update error state if this is still the latest request
      if (fetchId === latestFolderFetchId) {
        set({ error: (error as Error).message, isLoading: false })
      }
    }
  },

  setCurrentFolder: (folder) => {
    set({ currentFolder: folder })
  },

  clearError: () => {
    set({ error: null })
  },
}))
